import React from 'react';
import facebookIcon from '../../assets/img/facebook.svg';
import fscsImg from '../../assets/img/fscs-banner.png';
import twitterIcon from '../../assets/img/twitter.svg';
import { useBrand } from '../../providers';
import { Brand } from '../../types/global';
import ExternalLink from '../ExternalLink';
import ScreenReaderOnly from '../ScreenReaderOnly/ScreenReaderOnly';
import './Footer.scss';

const copyrightEntity: Record<Brand, string> = {
  [Brand.NatWest]: 'National Westminster Bank plc',
  [Brand.RoyalBankOfScotland]: 'Royal Bank of Scotland plc'
};

const cookiesUrls: Record<Brand, string> = {
  [Brand.NatWest]: 'https://www.natwest.com/global/cookie-privacy.html?channel=personal',
  [Brand.RoyalBankOfScotland]: 'https://www.rbs.co.uk/global/cookie-privacy.html?channel=personal'
};

const termsUrls: Record<Brand, string> = {
  [Brand.NatWest]: 'https://www.natwest.com/website-terms-and-FSCS.html',
  [Brand.RoyalBankOfScotland]: 'https://www.rbs.co.uk/website-terms-and-FSCS.html'
};

const accessibilityUrls: Record<Brand, string> = {
  [Brand.NatWest]: 'https://www.natwest.com/accessibility.html',
  [Brand.RoyalBankOfScotland]: 'https://www.rbs.co.uk/accessibility.html'
};

const fscsUrls: Record<Brand, string> = {
  [Brand.NatWest]:
    'https://www.natwest.com/content/dam/natwest/business/current-accounts/documents/FSCS_leaflet.pdf',
  [Brand.RoyalBankOfScotland]:
    'https://www.rbs.co.uk/content/dam/natwest/business/current-accounts/documents/FSCS_leaflet.pdf'
};

const facebookUrls: Record<Brand, string> = {
  [Brand.NatWest]: 'http://www.facebook.com/NatWest',
  [Brand.RoyalBankOfScotland]: 'http://www.facebook.com/royalbankofscotland'
};

const twitterUrls: Record<Brand, string> = {
  [Brand.NatWest]: 'https://twitter.com/natwest_help',
  [Brand.RoyalBankOfScotland]: 'http://twitter.com/rbs_help'
};

const registeredOffice = {
  [Brand.NatWest]: '250 Bishopsgate, London, EC2M 4AA',
  [Brand.RoyalBankOfScotland]: '36 St Andrew Square, Edinburgh, EH2 2YB'
};

export interface FooterProps {
  hideSocialLinks?: boolean;
  hideFSCS?: boolean;
}

const Footer: React.FC<FooterProps> = ({ hideSocialLinks = false, hideFSCS = false }) => {
  const { brand } = useBrand();

  return (
    <footer className="zb-footer mss-footer" role="contentinfo">
      <div className="zb-container">
        <ul className="mss-footer__links">
          <li>
            <ExternalLink href={cookiesUrls[brand]}>Privacy &amp; Cookies</ExternalLink>
          </li>
          <li>
            <ExternalLink href={termsUrls[brand]}>Website T&amp;Cs &amp; FSCS</ExternalLink>
          </li>
          <li>
            <ExternalLink href={accessibilityUrls[brand]}>Accessibility</ExternalLink>
          </li>
        </ul>
        {(!hideSocialLinks || !hideFSCS) && (
          <div className="mss-footer__row">
            {!hideSocialLinks && (
              <ul className="mss-footer__social">
                <li>
                  <ExternalLink href={facebookUrls[brand]}>
                    <img src={facebookIcon as string} alt="" />
                    <ScreenReaderOnly>Facebook</ScreenReaderOnly>
                  </ExternalLink>
                </li>
                <li>
                  <ExternalLink href={twitterUrls[brand]}>
                    <img src={twitterIcon as string} alt="" />
                    <ScreenReaderOnly>Twitter</ScreenReaderOnly>
                  </ExternalLink>
                </li>
              </ul>
            )}
            {!hideFSCS && (
              <ExternalLink href={fscsUrls[brand]}>
                <img src={fscsImg as string} className="mss-footer__fscs-logo" alt="" />
                <ScreenReaderOnly>Download FSCS Protecting Your Money Leaflet</ScreenReaderOnly>
              </ExternalLink>
            )}
          </div>
        )}
        <span className="zb-footer-text">
          Copyright {`\u{00A9} ${new Date().getFullYear()} ${copyrightEntity[brand]}`}. Registered
          office: {registeredOffice[brand]}.
        </span>
      </div>
    </footer>
  );
};

export default Footer;
